import { nextTick } from 'vue';
import { refreshCookie } from '#app/composables/cookie';
import { useServiciosStore } from './servicios';
import type { IUserConf, Prettify } from '~~/interfaces';
import {
  BASE_SERVICIO_SLUG,
  DEFAULT_LIMIT,
  DEFAULT_THEME,
  MAX_AGE_COOKIE,
  MAX_COOKIE_SIZE,
  type TServiciosSlug,
} from '~~/constants';
import {
  encodeUserPreferences,
  fitUserPreferencesToCookie,
  mergeLegacyStations,
  normalizeUserPreferences,
  parseLegacyEstaciones,
  parseUserPreferences,
  type StoredUserPreferences,
  validateServicioSlug,
} from '~~/helpers/preferences';

/**
 * Validated user preferences exposed to consumers (middleware, pages).
 * `servicio` is a concrete slug once validated; the raw cookie may hold null.
 * The `estaciones` field maps each service slug to saved station refs and is
 * persisted in the `mb_user` cookie; it replaces the old `mb_estaciones`
 * localStorage map.
 */
export type UserPreferences = {
  estaciones: Record<string, string[]>;
  limite: number;
  servicio: TServiciosSlug;
  theme: 'dark' | 'light';
};

export const useUserStore = defineStore('user', () => {
  const servicios = useServiciosStore();
  const router = useRouter();
  const secureCookie = import.meta.client
    ? window.location.protocol === 'https:'
    : process.env.NODE_ENV === 'production';

  // No `servicio` is forced here: a new user (no cookie) gets null, so
  // saved-state reads can distinguish "nothing saved" from an explicit save.
  const defaultPreferences: StoredUserPreferences = {
    estaciones: {},
    limite: DEFAULT_LIMIT,
    servicio: null,
    theme: DEFAULT_THEME,
  };

  const cookie = useCookie<StoredUserPreferences | null>('mb_user', {
    refresh: true,
    maxAge: MAX_AGE_COOKIE,
    path: '/',
    sameSite: 'lax',
    secure: secureCookie,
    // Client writes are handled centrally below so useCookie cannot race
    // them with a stale scheduled serialization.
    watch: false,
  });
  const currentPreferences = computed(
    () => normalizeUserPreferences(cookie.value) ?? defaultPreferences
  );

  function writeUserPreferences(
    newValue: StoredUserPreferences
  ): StoredUserPreferences | null {
    const result = fitUserPreferencesToCookie(newValue, MAX_COOKIE_SIZE);
    if (!result.success) {
      console.warn('Failed to write user preferences:', result.reason);
      return null;
    }
    cookie.value = result.value;

    // Keep useCookie as the reactive and SSR source, while explicit client
    // actions persist synchronously so navigation can immediately read them.
    if (import.meta.client) {
      const encoded = encodeUserPreferences(result.value);
      const writeClientCookie = () => {
        const secure = secureCookie ? '; Secure' : '';
        try {
          document.cookie =
            `mb_user=${encoded}; Max-Age=${MAX_AGE_COOKIE}; Path=/; ` +
            `SameSite=Lax${secure}`;
        } catch (error) {
          console.warn(
            'Failed to write user preferences to document.cookie:',
            error
          );
        }
      };

      writeClientCookie();
      refreshCookie('mb_user');
    }

    return result.value;
  }

  const estacionesServicio = computed(
    () => currentPreferences.value.estaciones
  );
  const estaciones = computed({
    get: () => {
      const estacionesSet = estacionesServicio.value[servicio.value];
      return Array.isArray(estacionesSet) ? estacionesSet : [];
    },
    set: (estaciones: string[]) => {
      writeUserPreferences({
        ...currentPreferences.value,
        estaciones: {
          ...currentPreferences.value.estaciones,
          [servicio.value]: estaciones,
        },
      });
    },
  });

  /**
   * Validated saved service slug persisted in the cookie, or null when the
   * cookie is absent, malformed, or holds an unknown/empty slug. This is the
   * only read the saved-state (root redirect) path should trust.
   */
  const savedServicio = computed<TServiciosSlug | null>(() => {
    const parsed = parseUserPreferences(cookie.value);
    return parsed?.servicio ?? null;
  });
  const hasSavedServicio = computed(() => savedServicio.value !== null);

  const selectServicio = async (newSrv: string): Promise<void> => {
    const validated = validateServicioSlug(newSrv);
    if (!validated) return;

    writeUserPreferences({
      ...currentPreferences.value,
      servicio: validated,
    });

    if (servicios.servicio !== validated) {
      servicios.cambiarServicio({ servicio: validated });
    }

    const currentRouteServicio = router.currentRoute?.value?.params?.servicio;
    if (currentRouteServicio !== validated) {
      await router.push(`/${validated}`);
    }
  };

  const servicio = computed({
    get: () => servicios.servicio || BASE_SERVICIO_SLUG,
    set: (newSrv) => {
      void selectServicio(newSrv);
    },
  });
  const limite = computed({
    get: () => currentPreferences.value.limite,
    set: (limite: number) => {
      writeUserPreferences({ ...currentPreferences.value, limite });
    },
  });
  const loadedConfigs = ref(false);
  const v = ref(1);
  const theme = computed({
    get: () => currentPreferences.value.theme,
    set: (theme: 'dark' | 'light') => {
      writeUserPreferences({ ...currentPreferences.value, theme });
    },
  });

  async function initDB() {}

  async function setPreferencia({
    key,
    value,
  }: {
    key: Prettify<keyof IUserConf>;
    value: Prettify<IUserConf[keyof IUserConf]>;
  }) {
    if (!key) throw new Error('Faltan datos');
    if (!Object.keys(defaultPreferences).includes(key))
      throw new Error('Preferencia no encontrada');

    switch (key) {
      case 'limite':
        if (typeof value !== 'number') throw new Error('Valor no válido');
        writeUserPreferences({ ...currentPreferences.value, limite: value });
        break;
      case 'servicio':
        if (typeof value !== 'string') throw new Error('Valor no válido');
        // Validate before persisting: skip writes for invalid slugs.
        if (!validateServicioSlug(value)) return;
        await selectServicio(value);
        break;
      case 'theme':
        if (value !== 'dark' && value !== 'light')
          throw new Error('Valor no válido');
        writeUserPreferences({ ...currentPreferences.value, theme: value });
        break;
      default:
        throw new Error('Preferencia no encontrada');
    }

    return;
  }

  function saveEstacion(
    estacion: string,
    servicio: TServiciosSlug = BASE_SERVICIO_SLUG
  ) {
    if (!servicio) return;
    if (!estacion) return;

    // The cookie is untrusted (user-editable JSON), so guard against a
    // missing or malformed `estaciones` map and non-array service values.
    const estacionesMap = currentPreferences.value.estaciones;
    const current = Array.isArray(estacionesMap[servicio])
      ? estacionesMap[servicio]
      : [];
    const updatedMap = { ...estacionesMap };

    const remove = current.includes(estacion);
    const updated = remove
      ? current.filter((e) => e !== estacion)
      : [...current, estacion];

    updatedMap[servicio] = updated;

    writeUserPreferences({
      ...currentPreferences.value,
      estaciones: updatedMap,
    });

    return updated;
  }

  function readRawCookieValue(name: string): string | undefined {
    if (typeof document === 'undefined' || !document.cookie) return undefined;
    const matches = [
      ...document.cookie.matchAll(new RegExp(`(?:^|; )${name}=([^;]*)`, 'g')),
    ];
    return matches.at(-1)?.[1];
  }

  async function migrateLegacyStations(): Promise<void> {
    if (!import.meta.client) return;

    let fittedValue: StoredUserPreferences | null = null;

    try {
      const raw = window.localStorage.getItem('mb_estaciones');
      if (!raw) return;

      const legacy = parseLegacyEstaciones(raw);
      if (!legacy) return;

      const merged = mergeLegacyStations(
        currentPreferences.value.estaciones,
        legacy
      );
      fittedValue = writeUserPreferences({
        ...currentPreferences.value,
        estaciones: merged,
      });

      if (!fittedValue) return;
    } catch (error) {
      console.warn('Failed to migrate legacy stations:', error);
      return;
    }

    try {
      await nextTick();
      const rawCookie = readRawCookieValue('mb_user');
      if (rawCookie === encodeUserPreferences(fittedValue)) {
        window.localStorage.removeItem('mb_estaciones');
      }
    } catch (error) {
      console.warn('Failed to verify migrated cookie persistence:', error);
    }
  }

  if (import.meta.client) {
    onNuxtReady(() => void migrateLegacyStations());
  }

  return {
    estaciones,
    estacionesServicio,
    servicio,
    savedServicio,
    hasSavedServicio,
    limite,
    loadedConfigs,
    v,
    theme,

    initDB,
    setPreferencia,
    selectServicio,
    saveEstacion,
  };
});
